"use client";

// Coded visuals for the location value sections, styled to match the home page product visuals.
// WhyChooseVisual reuses the ACTUAL product-card 3D visuals — the residential house + the mobile
// phone — since locations offer residential everywhere plus mobile worldwide. ReliableVisual is a local
// SVG/CSS piece. No images: pure CSS/SVG so they stay crisp and dependency-free.

import { useState } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Activity, Gauge, Wifi } from "@/components/icons";
import { GREEN } from "@/lib/constants";
import { ResidentialProxiesVisual } from "@/components/sections/home/products-overview/residential-proxies-visual";
import { MobileProxiesVisual } from "@/components/sections/home/products-overview/mobile-proxies-visual";

type IconComponent = React.ComponentType<{ size?: number; style?: React.CSSProperties }>;

// "Why Proxidize" — shows the two proxy types you get at a location using the exact 3D visuals from
// the product cards: the residential house (available everywhere) and the mobile phone (worldwide). One
// shared hover tilts both isometric scenes together.
export function WhyChooseVisual() {
  const reduced = useReducedMotion() ?? false;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className="border-border bg-card divide-border relative grid aspect-3/2 w-full grid-cols-2 divide-x overflow-hidden rounded-2xl border"
    >
      <div className="relative">
        <ResidentialProxiesVisual reduced={reduced} hovered={hovered} />
      </div>
      <div className="relative">
        <MobileProxiesVisual reduced={reduced} hovered={hovered} />
      </div>
    </div>
  );
}

const RELIABLE_ROWS: { icon: IconComponent; label: string }[] = [
  { icon: Activity, label: "Stable sessions" },
  { icon: Wifi, label: "Real network access" },
  { icon: Gauge, label: "Consistent connectivity" },
];

export function ReliableVisual() {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={scrollRevealViewport}
      transition={{ duration: 0.5 }}
      className="border-border bg-card relative overflow-hidden rounded-2xl border p-6"
    >
      <div
        className="pointer-events-none absolute -top-16 -right-16 size-48 rounded-full opacity-60 blur-3xl"
        style={{ background: `${GREEN}1f` }}
        aria-hidden="true"
      />
      <div className="relative mb-6 flex items-end justify-between">
        <div>
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Uptime
          </p>
          <p className="text-foreground text-4xl font-bold tracking-tight">99.9%</p>
        </div>
        <div className="flex items-end gap-1.5" aria-hidden="true">
          {[36, 56, 76, 100].map((h, i) => (
            <motion.span
              key={h}
              className="w-2 rounded-full"
              style={{ background: GREEN, opacity: 0.35 + i * 0.2 }}
              initial={reduced ? false : { height: 6 }}
              whileInView={{ height: `${h}%` }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
            />
          ))}
        </div>
      </div>
      <ul className="relative space-y-2.5">
        {RELIABLE_ROWS.map(({ icon: Icon, label }) => (
          <li
            key={label}
            className="border-border/60 bg-foreground/2 flex items-center gap-3 rounded-xl border px-3 py-2.5"
          >
            <Icon size={18} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
            <span className="text-foreground text-sm font-medium">{label}</span>
            <span className="ml-auto flex items-center gap-1.5">
              <span
                className="size-2 rounded-full"
                style={{ background: GREEN }}
                aria-hidden="true"
              />
              <span className="text-muted-foreground text-xs">Live</span>
            </span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
